import { SupportedLinkPreview } from './types';

type AppleMusicResourceKind = 'album' | 'artist' | 'music-video' | 'playlist' | 'song';

const APPLE_MUSIC_EYEBROW = 'Apple Music';
const APPLE_MUSIC_HOSTS = new Set(['itunes.apple.com', 'music.apple.com']);

const isStorefrontSegment = (value: string) => /^[a-z]{2}(?:-[a-z]{2})?$/i.test(value);

const toResourceKind = (resourceSegment: string, trackId: string | null): AppleMusicResourceKind | null => {
  if (trackId) {
    return 'song';
  }

  switch (resourceSegment.toLowerCase()) {
    case 'album':
      return 'album';
    case 'artist':
      return 'artist';
    case 'song':
      return 'song';
    case 'music-video':
      return 'music-video';
    case 'playlist':
      return 'playlist';
    default:
      return null;
  }
};

const toResourceLabel = (resourceKind: AppleMusicResourceKind): string => {
  switch (resourceKind) {
    case 'album':
      return 'Album';
    case 'artist':
      return 'Artist';
    case 'music-video':
      return 'Music Video';
    case 'playlist':
      return 'Playlist';
    case 'song':
      return 'Song';
  }
};

const toTitleFromSlug = (value: string): string => {
  const decoded = decodeURIComponent(value.replace(/\+/g, ' '));
  const normalized = decoded.replace(/[-_]+/g, ' ').replace(/\s+/g, ' ').trim();

  if (!normalized) {
    return APPLE_MUSIC_EYEBROW;
  }

  return normalized.replace(/\b\w/g, (character) => character.toUpperCase());
};

const findStorefront = (segments: string[]): string | null => {
  if (segments.length < 2 || !isStorefrontSegment(segments[0])) {
    return null;
  }

  return segments[0].toUpperCase();
};

export const buildAppleMusicPreview = (url: string): SupportedLinkPreview | null => {
  try {
    const parsedUrl = new URL(url);
    const hostname = parsedUrl.hostname.replace(/^www\./i, '').toLowerCase();

    if (!APPLE_MUSIC_HOSTS.has(hostname)) {
      return null;
    }

    const pathSegments = parsedUrl.pathname.split('/').filter(Boolean);
    const storefront = findStorefront(pathSegments);
    const resourceIndex = storefront ? 1 : 0;
    const slugIndex = resourceIndex + 1;
    const resourceSegment = pathSegments[resourceIndex] || '';
    const titleSegment = pathSegments[slugIndex] || resourceSegment || '';
    const trackId = parsedUrl.searchParams.get('i');
    const resourceKind = toResourceKind(resourceSegment, trackId);

    if (!resourceKind) {
      return null;
    }

    const title = toTitleFromSlug(titleSegment);
    const metaParts = [
      toResourceLabel(resourceKind),
      storefront ? `${storefront} Store` : '',
    ].filter(Boolean);

    return {
      ariaLabel: `Open ${title} in Apple Music`,
      artworkUrl: '',
      canonicalUrl: parsedUrl.toString(),
      eyebrow: APPLE_MUSIC_EYEBROW,
      meta: metaParts.join(' · '),
      provider: 'apple-music',
      resourceType: resourceKind,
      subtitle: 'Open in Apple Music',
      title,
    };
  } catch (error) {
    return null;
  }
};
